import { ReactNode, useEffect, useMemo, useRef } from "react";

import { useDockedWindows, useWindows } from "contexts/windows";

import { DOCK_PANEL_WIDTH } from "./constants";
import { useDockDragController } from "./dockDragContext";
import { DockLayoutContext } from "./dockLayoutContext";
import { type DockPhantom, useDockLayout } from "./useDockLayout";

export function DockLayoutProvider({ children }: { children: ReactNode }) {
    const dockedWindows = useDockedWindows();
    const windows = useWindows();
    const controller = useDockDragController();
    const { isDragging, draggingId, isInZone, insertionIndex } = controller.state;

    const anyWindowsDocked = useMemo(() => Object.values(windows).some((entry) => entry.state.docked), [windows]);

    const prevAnyDockedRef = useRef(anyWindowsDocked);
    useEffect(() => {
        if (prevAnyDockedRef.current && !anyWindowsDocked) {
            controller.setDockWidth(DOCK_PANEL_WIDTH);
        }
        prevAnyDockedRef.current = anyWindowsDocked;
    }, [anyWindowsDocked, controller]);
    const phantom = useMemo<DockPhantom | null>(() => {
        if (!isDragging || !isInZone || insertionIndex == null) {
            return null;
        }
        return { id: draggingId ?? "incoming", index: insertionIndex };
    }, [isDragging, isInZone, insertionIndex, draggingId]);

    const layout = useDockLayout(dockedWindows, phantom);
    return <DockLayoutContext.Provider value={layout}>{children}</DockLayoutContext.Provider>;
}
